import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  ArrowLeft,
  ChevronRight,
  Home,
  DollarSign,
  Users,
  Package,
  TrendingUp
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import expenseCategoryMasterService from '../../services/expenseCategoryMasterService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const ExpenseCategoryMaster = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalCategories: 0,
    activeCategories: 0,
    inactiveCategories: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [categoriesResponse, statsResponse] = await Promise.all([
        expenseCategoryMasterService.getAllCategories(),
        expenseCategoryMasterService.getCategoryStats()
      ])
      
      if (categoriesResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (categoriesResponse.data || []).map(category => ({
          ...category,
          id: category._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (categoriesResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No expense category records found. Create your first category!')
        }
      } else {
        toast.error(categoriesResponse.message || 'Failed to load categories')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading expense category master records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view categories')
      } else {
        toast.error('Failed to load expense category master records')
      }
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      accessorKey: 'categoryCode',
      header: 'Category Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'categoryName',
      header: 'Category Name',
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'categoryType',
      header: 'Type',
      cell: ({ getValue }) => (
        <div className="sac-cell" style={{ textAlign: 'center' }}>
          <span className="sac-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getValue()?.toLowerCase() === 'active' ? 'active' : 'inactive'}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'requiresReceipt',
      header: 'Requires Receipt',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getValue() ? 'active' : 'inactive'}`}>
            {getValue() ? 'Yes' : 'No'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'requiresApproval',
      header: 'Requires Approval',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getValue() ? 'active' : 'inactive'}`}>
            {getValue() ? 'Yes' : 'No'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/expenses/expense-category-master/form')
  }

  const handleEdit = (categoryData) => {
    navigate('/expenses/expense-category-master/edit', { state: { categoryData } })
  }

  const handleView = (categoryData) => {
    navigate('/expenses/expense-category-master/view', { state: { categoryData } })
  }

  const handleDelete = (categoryData) => {
    setDeleteItem(categoryData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting category...')
      // Use _id instead of id
      const response = await expenseCategoryMasterService.deleteCategory(deleteItem._id)
      if (response.success) {
        toast.success('Category deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete category')
      }
    } catch (error) {
      console.error('Error deleting category:', error)
      if (error.response?.status === 404) {
        toast.error('Category not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this category')
      } else {
        toast.error('Failed to delete category. Please try again.')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    // TODO: Implement export functionality
    toast.info('Export functionality coming soon!')
  }

  const breadcrumbItems = [
    {
      label: 'Expenses',
      path: '/expenses',
      onClick: () => navigate('/expenses')
    },
    {
      label: 'Expense Category Master',
      path: '/expenses/expense-category-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Expense Category Master</h1>
          <p>Manage expense types and classification</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            Add Category
          </button>
        </div>
      </div>

      {/* Metrics Section */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.totalCategories}</span>
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalCategories}</div>
          <div className="metric-title">Total Categories</div>
          <div className="metric-subtitle">All categories</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.activeCategories}</span>
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeCategories}</div>
          <div className="metric-title">Active Categories</div>
          <div className="metric-subtitle">Currently active</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.inactiveCategories}</span>
            </div>
            <div className="metric-trend negative">
              <span>Inactive</span>
            </div>
          </div>
          <div className="metric-value">{stats.inactiveCategories}</div>
          <div className="metric-title">Inactive Categories</div>
          <div className="metric-subtitle">Currently inactive</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onExport={handleExport}
          noCard={true}
          className="sac-master-table"
          pageSize={10}
        />
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Category"
        message={`Are you sure you want to delete "${deleteItem?.categoryName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default ExpenseCategoryMaster

